import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Download, Archive, FileText, Clock, User,
  Shield, CheckCircle, XCircle, AlertTriangle, RotateCcw,
  Eye, Loader2, Bot, Send, AlertCircle, ChevronRight,
  ThumbsUp, ThumbsDown, Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_STYLE = {
  not_opened: "bg-slate-100 text-slate-600 border-slate-200",
  opened: "bg-blue-100 text-blue-600 border-blue-200",
  recommended: "bg-yellow-100 text-yellow-600 border-yellow-200",
  qc_passed: "bg-orange-100 text-orange-600 border-orange-200",
  qc_failed: "bg-red-100 text-red-600 border-red-200",
  approved: "bg-green-100 text-green-600 border-green-200",
  rejected: "bg-red-100 text-red-600 border-red-200",
  returned: "bg-purple-100 text-purple-600 border-purple-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const STATUS_LABEL = {
  not_opened: "Not Opened", opened: "Opened",
  recommended: "Recommended", qc_passed: "QC Passed",
  qc_failed: "QC Failed", approved: "Approved",
  rejected: "Rejected", returned: "Returned", archived: "Archived",
};

const ACTION_ICONS = {
  created: FileText, opened: Eye, submitted: Send,
  recommended: ThumbsUp, qc_passed: CheckCircle,
  qc_failed: XCircle, approved: CheckCircle,
  rejected: XCircle, returned: Undo2,
  archived: Archive, cancelled: XCircle,
};

const ACTION_COLORS = {
  created: "text-slate-500", opened: "text-blue-500",
  submitted: "text-emerald-500", recommended: "text-amber-500",
  qc_passed: "text-orange-500", qc_failed: "text-red-500",
  approved: "text-green-500", rejected: "text-red-500",
  returned: "text-purple-500", archived: "text-slate-400",
  cancelled: "text-slate-400",
};

const WORKFLOW_STEPS = [
  { key: "recommend", label: "Recommendation", icon: ThumbsUp },
  { key: "qc", label: "Quality Control", icon: Shield },
  { key: "approve", label: "Approval", icon: CheckCircle },
];

const ACTIONS_BY_ROLE = {
  recommender: [
    { action: "recommend", label: "Recommend", icon: ThumbsUp, color: "bg-amber-600 hover:bg-amber-500" },
  ],
  qc: [
    { action: "qc_pass", label: "QC Pass", icon: CheckCircle, color: "bg-orange-600 hover:bg-orange-500" },
    { action: "qc_fail", label: "QC Fail", icon: XCircle, color: "bg-red-600 hover:bg-red-500" },
    { action: "return", label: "Return for Review", icon: Undo2, color: "bg-purple-600 hover:bg-purple-500" },
  ],
  approver: [
    { action: "approve", label: "Approve", icon: CheckCircle, color: "bg-green-600 hover:bg-green-500" },
    { action: "reject", label: "Reject", icon: XCircle, color: "bg-red-600 hover:bg-red-500" },
    { action: "return", label: "Return for Review", icon: Undo2, color: "bg-purple-600 hover:bg-purple-500" },
  ],
  creator: [
    { action: "resubmit", label: "Resubmit", icon: RotateCcw, color: "bg-blue-600 hover:bg-blue-500" },
  ],
};

export default function SMSDocumentView() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionPassword, setActionPassword] = useState("");
  const [actionComment, setActionComment] = useState("");
  const [actioning, setActioning] = useState(false);
  const [agentQuery, setAgentQuery] = useState("");
  const [agentResponse, setAgentResponse] = useState("");
  const [agentLoading, setAgentLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchDocument();
  }, [docId]);

  const fetchDocument = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sms/documents/${docId}`, {
        headers: getHeaders(), withCredentials: true,
      });
      setDoc(res.data.document);
      setAudit(res.data.audit_trail || []);
    } catch (err) {
      toast.error("Document not found");
      navigate("/hr-hub/smart-management/dashboard");
    }
    setLoading(false);
  };

  const getAvailableActions = () => {
    if (!doc) return [];
    const roleActions = ACTIONS_BY_ROLE[doc.my_role] || [];
    const allowedActions = [];

    if (doc.status === "not_opened" || doc.status === "opened") {
      allowedActions.push("recommend");
    } else if (doc.status === "recommended") {
      allowedActions.push("qc_pass", "qc_fail", "return");
    } else if (doc.status === "qc_passed" || doc.status === "qc_failed") {
      allowedActions.push("approve", "reject", "return");
    } else if (doc.status === "returned") {
      allowedActions.push("resubmit");
    }

    return roleActions.filter(a => allowedActions.includes(a.action));
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    if (!actionPassword) {
      toast.error("Password is required");
      return;
    }
    if ((actionDialog === "return" || actionDialog === "reject") && !actionComment.trim()) {
      toast.error("Comment is required for this action");
      return;
    }

    setActioning(true);
    try {
      const res = await axios.post(`${API}/sms/documents/${docId}/action`,
        { action: actionDialog, comment: actionComment, password: actionPassword },
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success(res.data.message || "Action completed");
      setActionDialog(null);
      setActionPassword("");
      setActionComment("");
      fetchDocument();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
    setActioning(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/sms/documents/${docId}/download`, {
        headers: getHeaders(), withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc?.file_name || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download not available");
    }
    setDownloading(false);
  };

  const handleArchive = async () => {
    try {
      await axios.post(`${API}/sms/documents/${docId}/archive`, {},
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success("Document archived");
      fetchDocument();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    }
  };

  const handleAgentSubmit = async (e) => {
    e.preventDefault();
    if (!agentQuery.trim()) return;
    setAgentLoading(true);
    setAgentResponse("");
    try {
      const res = await axios.post(`${API}/sms/agent`,
        { query: agentQuery, doc_id: docId },
        { headers: getHeaders(), withCredentials: true },
      );
      setAgentResponse(res.data.response);
    } catch {
      setAgentResponse("Sorry, I encountered an error.");
    }
    setAgentLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!doc) return null;

  const isOverdue = doc.days_remaining !== null && doc.days_remaining < 0
    && !["approved", "rejected", "archived"].includes(doc.status);
  const availableActions = getAvailableActions();
  const canArchive = doc.my_role === "creator" && !doc.is_archived && !["approved", "rejected"].includes(doc.status);
  const canDownload = doc.status === "approved" && doc.file_path;
  const showActionButton = actionDialog !== null;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none"
            onClick={() => navigate("/hr-hub/smart-management/dashboard")}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800 tracking-wide">
                {doc.doc_id}
              </h1>
              <Badge variant="outline"
                className={`text-[10px] px-1.5 py-0 font-medium border ${STATUS_STYLE[doc.status] || ""}`}
              >
                {STATUS_LABEL[doc.status] || doc.status}
              </Badge>
              {isOverdue && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-200 bg-red-50 text-red-600 animate-pulse">
                  OVERDUE
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500">{doc.doc_type_name} &middot; Created by {doc.created_by_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canDownload && (
            <Button size="sm" variant="outline" className="h-8 text-xs rounded-none" onClick={handleDownload} disabled={downloading}>
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
          )}
          {canArchive && (
            <Button size="sm" variant="outline" className="h-8 text-xs rounded-none text-slate-500" onClick={handleArchive}>
              <Archive className="w-3.5 h-3.5 mr-1" /> Archive
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Main content */}
        <div className="col-span-2 space-y-4">
          {/* Document Info Card */}
          <Card className="bg-white border-slate-200 p-4">
            <h2 className="font-['Barlow_Condensed'] text-sm font-bold text-slate-800 mb-3">Document Information</h2>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-slate-400">Name:</span> <span className="text-slate-700 font-medium">{doc.name}</span></div>
              <div><span className="text-slate-400">Document ID:</span> <span className="font-mono text-slate-700">{doc.doc_id}</span></div>
              <div><span className="text-slate-400">Type:</span> <span className="text-slate-700">{doc.doc_type_name}</span></div>
              <div><span className="text-slate-400">Created:</span> <span className="text-slate-700">{doc.created_at?.slice(0, 19).replace("T", " ")}</span></div>
              {doc.expected_action_date && (
                <div>
                  <span className="text-slate-400">Expected by:</span>{" "}
                  <span className={isOverdue ? "text-red-600 font-semibold" : "text-slate-700"}>
                    {doc.expected_action_date?.slice(0, 10)}
                    {doc.days_remaining !== null && (doc.days_remaining >= 0
                      ? ` (${doc.days_remaining}d remaining)`
                      : ` (${Math.abs(doc.days_remaining)}d overdue)`)}
                  </span>
                </div>
              )}
              {doc.description && (
                <div className="col-span-2">
                  <span className="text-slate-400">Description:</span>
                  <p className="text-slate-600 mt-0.5">{doc.description}</p>
                </div>
              )}
              {doc.notes && (
                <div className="col-span-2">
                  <span className="text-slate-400">Notes:</span>
                  <p className="text-slate-600 mt-0.5">{doc.notes}</p>
                </div>
              )}
              {doc.file_name && (
                <div className="col-span-2 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-slate-600">{doc.file_name}</span>
                  <span className="text-slate-300">({(doc.file_size / 1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>
          </Card>

          {/* Workflow Timeline */}
          <Card className="bg-white border-slate-200 p-4">
            <h2 className="font-['Barlow_Condensed'] text-sm font-bold text-slate-800 mb-3">Workflow Status</h2>
            <div className="flex items-center gap-2">
              {doc.workflow_stages?.map((stage, idx) => {
                const stepInfo = WORKFLOW_STEPS.find(s => s.key === stage);
                const StepIcon = stepInfo?.icon || ChevronRight;
                const isComplete = idx < doc.current_step;
                const isCurrent = idx === doc.current_step;
                const isPending = idx > doc.current_step;

                let stageStatus = doc.status;
                if (stage === "recommend" && ["recommended", "qc_passed", "qc_failed", "approved", "rejected"].includes(doc.status)) {
                  // Check: was it recommended?
                  const hasRecommended = audit.some(a => a.action === "recommended");
                  if (hasRecommended) isComplete = true;
                }

                return (
                  <div key={stage} className="flex items-center gap-2 flex-1">
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 border text-xs font-medium ${
                      isCurrent ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : isComplete ? 'bg-green-50 border-green-200 text-green-600'
                        : 'bg-app border-slate-200 text-slate-400'
                    }`}>
                      <StepIcon className="w-3.5 h-3.5" />
                      <span>{stepInfo?.label || stage}</span>
                    </div>
                    {idx < doc.workflow_stages.length - 1 && (
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                    )}
                  </div>
                );
              })}
              <div className={`flex items-center gap-1.5 px-3 py-1.5 border text-xs font-medium ${
                doc.status === "approved" ? 'bg-green-50 border-green-200 text-green-600'
                  : doc.status === "rejected" ? 'bg-red-50 border-red-200 text-red-600'
                  : 'bg-app border-slate-200 text-slate-300'
              }`}>
                <CheckCircle className="w-3.5 h-3.5" />
                <span>Complete</span>
              </div>
            </div>

            {/* Role assignments */}
            <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-100">
              <div className="text-xs">
                <Label className="text-[10px] text-slate-400">Recommenders</Label>
                <div className="mt-1 space-y-0.5">
                  {doc.recommender_names?.length > 0 ? doc.recommender_names.map((n, i) => (
                    <p key={i} className="text-slate-700">{n}</p>
                  )) : <p className="text-slate-300">None assigned</p>}
                </div>
              </div>
              <div className="text-xs">
                <Label className="text-[10px] text-slate-400">Quality Controller</Label>
                <p className="mt-1 text-slate-700">{doc.qc_name || "Not assigned"}</p>
              </div>
              <div className="text-xs">
                <Label className="text-[10px] text-slate-400">Approver</Label>
                <p className="mt-1 text-slate-700">{doc.approver_name || "Not assigned"}</p>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          {availableActions.length > 0 && (
            <Card className="bg-white border-slate-200 p-4">
              <h2 className="font-['Barlow_Condensed'] text-sm font-bold text-slate-800 mb-3">Actions</h2>
              <div className="flex items-center gap-2">
                {availableActions.map((act) => {
                  const ActIcon = act.icon;
                  return (
                    <Button key={act.action} size="sm"
                      className={`h-8 text-xs rounded-none text-white ${act.color}`}
                      onClick={() => setActionDialog(act.action)}
                    >
                      <ActIcon className="w-3.5 h-3.5 mr-1" />
                      {act.label}
                    </Button>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Audit Trail */}
          <Card className="bg-white border-slate-200 p-4">
            <h2 className="font-['Barlow_Condensed'] text-sm font-bold text-slate-800 mb-3">Audit Trail</h2>
            {audit.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">No activity recorded yet</p>
            ) : (
              <div className="space-y-1.5">
                {audit.map((entry) => {
                  const AIcon = ACTION_ICONS[entry.action] || Eye;
                  const colorClass = ACTION_COLORS[entry.action] || "text-slate-500";
                  return (
                    <div key={entry.id} className="flex items-start gap-2 text-xs py-1.5 border-b border-slate-50 last:border-0">
                      <AIcon className={`w-3.5 h-3.5 mt-0.5 ${colorClass}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">{entry.actor_name}</span>
                          <span className="text-slate-400">{STATUS_LABEL[entry.action] || entry.action}</span>
                          <span className="text-slate-300 ml-auto">
                            {entry.created_at?.slice(0, 19).replace("T", " ")}
                          </span>
                        </div>
                        {entry.comment && (
                          <p className="text-slate-500 mt-0.5 italic">&ldquo;{entry.comment}&rdquo;</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar - Agent SMS */}
        <div className="space-y-4">
          <Card className="bg-white border-slate-200">
            <div className="p-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-purple-600" />
                <span className="font-['Barlow_Condensed'] text-sm font-bold text-slate-800">Agent SMS</span>
              </div>
            </div>
            <div className="p-3">
              {agentResponse && (
                <div className="bg-purple-50 border border-purple-200 rounded p-3 text-xs text-purple-800 mb-3 whitespace-pre-wrap">
                  {agentResponse}
                </div>
              )}
              {agentLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                </div>
              )}
              <form onSubmit={handleAgentSubmit} className="flex gap-2">
                <Input value={agentQuery} onChange={e => setAgentQuery(e.target.value)}
                  placeholder="Ask about this document..." className="h-8 text-xs rounded-none flex-1"
                />
                <Button type="submit" size="sm" className="h-8 w-8 p-0 rounded-none bg-purple-600 hover:bg-purple-500">
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </form>
            </div>
            <div className="p-3 bg-app text-[10px] text-slate-400 space-y-1">
              <p>Try: "What is this document about?"</p>
              <p>Try: "Who still needs to approve?"</p>
              <p>Try: "Summarize the workflow status"</p>
            </div>
          </Card>
        </div>
      </div>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(open) => !open && setActionDialog(null)}>
        <DialogContent className="sm:max-w-md bg-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg tracking-wide">
              {actionDialog === "recommend" && "Confirm Recommendation"}
              {actionDialog === "qc_pass" && "Confirm QC Pass"}
              {actionDialog === "qc_fail" && "Confirm QC Fail"}
              {actionDialog === "approve" && "Confirm Approval"}
              {actionDialog === "reject" && "Confirm Rejection"}
              {actionDialog === "return" && "Return for Review"}
              {actionDialog === "resubmit" && "Confirm Resubmission"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Document: {doc?.doc_id} — {doc?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {(actionDialog === "return" || actionDialog === "reject" || actionDialog === "qc_fail") && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">
                  Comment * (required)
                </Label>
                <Textarea value={actionComment} onChange={e => setActionComment(e.target.value)}
                  placeholder="Provide a reason for this action..." rows={3}
                  className="rounded-none border-slate-300 text-xs"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">
                Password * (re-enter your login password)
              </Label>
              <Input type="password" value={actionPassword}
                onChange={e => setActionPassword(e.target.value)}
                placeholder="Your login password"
                className="rounded-none border-slate-300 h-9 text-xs"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 justify-end">
            <Button variant="outline" size="sm" className="h-8 text-xs rounded-none"
              onClick={() => { setActionDialog(null); setActionPassword(""); setActionComment(""); }}
            >
              Cancel
            </Button>
            <Button size="sm" className="h-8 text-xs rounded-none text-white bg-emerald-600 hover:bg-emerald-500"
              onClick={handleAction} disabled={actioning}
            >
              {actioning && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
